/**
 * rmaInspectionUtils.ts
 *
 * Pure helpers for the RMA inspection and warranty disposition form.
 */
import type {
  FailureCategory,
  InspectionDisposition,
  InspectionRequest,
} from "../../../api/shipmentApi";
import { sanitizeFreeText } from "./sanitizeFreeText";

export const INSPECTOR_NOTES_MIN_LENGTH = 15;
export const INSPECTOR_NOTES_MAX_LENGTH = 2000;

export const INSPECTION_MESSAGES = {
  declinedNeedsEvidence: "A declined disposition needs an end user caused failure category and inspector notes.",
  approvedNeedsDefect: "An approved disposition needs at least one defect failure category.",
  disposition: "Select a disposition: Approved or Declined.",
  vendorReference: "Enter the vendor reference number.",
  inspectionDate: "Enter the inspection date.",
  inspectorName: "Enter the inspector name.",
  deviceModel: "Enter the device model.",
  warrantyOrder: "The warranty end date cannot be before the warranty start date.",
} as const;

export interface InspectionFormState {
  vendorReference: string;
  inspectionDate: Date | null;
  inspectorName: string;
  deviceModel: string;
  warrantyStart: Date | null;
  warrantyEnd: Date | null;
  categoryCodes: string[];
  inspectorNotes: string;
  disposition: InspectionDisposition | null;
  reportBlobName: string | null;
}

export const buildInitialInspectionForm = (deviceModel?: string | null): InspectionFormState => ({
  vendorReference: "",
  inspectionDate: new Date(),
  inspectorName: "",
  deviceModel: deviceModel ?? "",
  warrantyStart: null,
  warrantyEnd: null,
  categoryCodes: [],
  inspectorNotes: "",
  disposition: null,
  reportBlobName: null,
});

/** Local calendar date as YYYY-MM-DD (no UTC shift). */
export const toIsoDate = (d: Date): string => {
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
};

const startOfDay = (d: Date): number => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

/** First problem with the form, in the order the backend checks them; null when it can be submitted. */
export const validateInspection = (form: InspectionFormState, categories: FailureCategory[]): string | null => {
  if (!form.vendorReference.trim()) return INSPECTION_MESSAGES.vendorReference;
  if (!form.inspectionDate) return INSPECTION_MESSAGES.inspectionDate;
  if (!form.inspectorName.trim()) return INSPECTION_MESSAGES.inspectorName;
  if (!form.deviceModel.trim()) return INSPECTION_MESSAGES.deviceModel;
  if (form.warrantyStart && form.warrantyEnd && startOfDay(form.warrantyEnd) < startOfDay(form.warrantyStart)) {
    return INSPECTION_MESSAGES.warrantyOrder;
  }
  if (!form.disposition) return INSPECTION_MESSAGES.disposition;

  const typeOf = (code: string) => categories.find((c) => c.code === code)?.type;
  const selected = form.categoryCodes.map(typeOf);
  if (form.disposition === "DECLINED") {
    const notes = sanitizeFreeText(form.inspectorNotes, INSPECTOR_NOTES_MAX_LENGTH);
    if (!selected.includes("END_USER") || notes.length < INSPECTOR_NOTES_MIN_LENGTH) {
      return INSPECTION_MESSAGES.declinedNeedsEvidence;
    }
  } else if (!selected.includes("DEFECT")) {
    return INSPECTION_MESSAGES.approvedNeedsDefect;
  }
  return null;
};

/** Call only after validateInspection returned null. */
export const buildInspectionRequest = (form: InspectionFormState): InspectionRequest => ({
  vendorReference: form.vendorReference.trim(),
  inspectionDate: toIsoDate(form.inspectionDate as Date),
  inspectorName: form.inspectorName.trim(),
  deviceModel: form.deviceModel.trim(),
  warrantyStart: form.warrantyStart ? toIsoDate(form.warrantyStart) : null,
  warrantyEnd: form.warrantyEnd ? toIsoDate(form.warrantyEnd) : null,
  categoryCodes: form.categoryCodes,
  inspectorNotes: sanitizeFreeText(form.inspectorNotes, INSPECTOR_NOTES_MAX_LENGTH) || null,
  disposition: form.disposition as InspectionDisposition,
  reportBlobName: form.reportBlobName,
});
